import Table from "@mui/material/Table";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import TableBody from "@mui/material/TableBody";
import SbuxStyledTablePagination from "./SbuxStyledTablePagination";
import SbuxTablePaginationActions from "./SbuxTablePaginationActions";
import SbuxTranslate from "../SbuxTranslate";
import SbuxEnhancedTableHead from "./SbuxEnhancedTableHead";
import { StyledGridOverlay } from "../SbuxDataGrid/styled";
import { Paper, Box, Button } from "@mui/material";
import { getRowsPerPage } from "../../features/content/utils";
import useTextTranslate from "../../features/content/hooks/useTextTranslate";
import styles from "./styles";
import useCss from "../../hooks/useCss";
import { ReactComponent as SbuxLogo } from "../../assets/image.svg";
import {
  selectionStrategyResolver,
  isSelectionStrategyEnabled,
  type SelectionStrategy,
} from "../../utils/tableUtil";

type Order = "asc" | "desc";

interface HeaderCell {
  id: string;
  label: string;
  align?: "left" | "right" | "center";
  disablePadding?: boolean;
  minWidth?: number;
  sortable?: boolean;
  hide?: boolean;
  checkSelector?: boolean;
  [key: string]: unknown;
}

interface Props {
  BodySource: React.ComponentType;
  headerGroupCells?: HeaderCell[] | null;
  headerCells: HeaderCell[];
  rows: Record<string, unknown>[];
  rowsPerPage: number;
  page: number;
  order: Order;
  orderBy: string;
  handleChangePage: (event: unknown, newPage: number) => void;
  handleChangeRowsPerPage: (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  handleRefreshClick: () => void;
  onRequestSort: (event: React.MouseEvent, property: string) => void;
  showFooter: boolean;
  showModalTable?: boolean;
  defaultRowsPerPage?: number;
  isManager?: boolean;
  isLoading?: boolean;
  isTranslationRequired?: boolean;
  handleCheckboxChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectionStrategy?: Record<string, unknown> | null;
  footerMarginBottom?: string;
}

const SbuxBasicTable = ({
  BodySource,
  headerGroupCells,
  headerCells,
  rows,
  rowsPerPage,
  page,
  order,
  orderBy,
  handleChangePage,
  handleChangeRowsPerPage,
  handleRefreshClick,
  onRequestSort,
  showFooter,
  showModalTable = false,
  defaultRowsPerPage,
  isManager = false,
  isLoading = false,
  isTranslationRequired = true,
  handleCheckboxChange,
  selectionStrategy = null,
  footerMarginBottom = "0",
}: Props) => {
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - rows.length) : 0;
  const classes = useCss(styles);

  const textOfTranslatedText = useTextTranslate("of");

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        // Non-modal: flex:1 so the table grows to fill its flex parent.
        // Modal: explicit vh heights are intentional - modals have no flex parent to fill.
        flex: showModalTable ? undefined : 1,
        height:
          showModalTable && isManager
            ? "35vh"
            : showModalTable
              ? "65vh"
              : undefined,
        overflow: "hidden",
      }}
    >
      <TableContainer
        className={classes.tableBodyWrapper} // Add this class
      >
        <Table
          stickyHeader
          aria-label="custom pagination table"
          // table-layout: fixed prevents column width reflow when rows are added/removed,
          // avoiding header height inflation in modal dialogs. Column widths are driven
          // by the <th> width attributes (detailColumns minWidth values).
          sx={showModalTable ? { tableLayout: "fixed" } : undefined}
        >
          <SbuxEnhancedTableHead
            headerGroupCells={headerGroupCells}
            headerCells={headerCells}
            order={order}
            orderBy={orderBy}
            onRequestSort={onRequestSort}
            isTranslationRequired={isTranslationRequired}
            handleCheckboxChange={handleCheckboxChange}
            selectAllCheckbox={{
              enabled: isSelectionStrategyEnabled(
                selectionStrategy as SelectionStrategy | null,
              ),
              checked: selectionStrategyResolver(
                selectionStrategy as SelectionStrategy | null,
                page,
              ),
            }}
          />

          <TableBody>
            {rows && rows.length === 0 && !isLoading ? (
              <TableRow>
                <TableCell colSpan={12} sx={{ border: "none" }}>
                  <Box
                    sx={{
                      marginTop: showModalTable && isManager ? "-20%" : "-10%",
                    }}
                  >
                    <StyledGridOverlay>
                      <SbuxLogo className={classes.sbuxLogo} />

                      <Box className={classes.dataNotFound}>
                        {isTranslationRequired ? (
                          <SbuxTranslate>{`No data found`}</SbuxTranslate>
                        ) : (
                          <Box>
                            <SbuxTranslate>{`No data found`}</SbuxTranslate>
                          </Box>
                        )}
                        <Box className="actions">
                          <Button
                            variant="contained"
                            size="medium"
                            onClick={handleRefreshClick}
                          >
                            {isTranslationRequired ? (
                              <SbuxTranslate>{`Refresh`}</SbuxTranslate>
                            ) : (
                              "Refresh"
                            )}
                          </Button>
                        </Box>
                      </Box>
                    </StyledGridOverlay>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              <>
                <BodySource />
                {emptyRows > 0 && <TableRow />}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showFooter && (
        <Box
          className={classes.tableFooter}
          sx={{
            marginBottom: footerMarginBottom,
          }}
        >
          <SbuxStyledTablePagination
            rowsPerPageOptions={getRowsPerPage(defaultRowsPerPage)}
            labelRowsPerPage={
              isTranslationRequired ? (
                <SbuxTranslate>{`Rows per page`}</SbuxTranslate>
              ) : (
                <>
                  <SbuxTranslate>{`Rows per page`}</SbuxTranslate>
                </>
              )
            }
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${
                to === -1 ? count : to
              } ${textOfTranslatedText} ${count}`
            }
            count={rows.length}
            rowsPerPage={rowsPerPage}
            page={page}
            SelectProps={{
              inputProps: { "aria-label": "rows per page" },
              MenuProps: {
                className: classes.pagination,
              },
            }}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={SbuxTablePaginationActions}
          />
        </Box>
      )}
    </Paper>
  );
};

export default SbuxBasicTable;
